import { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import NoticeBar from "@/components/NoticeBar";
import { AuthProvider } from "@/lib/auth-context";
import { AppearanceProvider } from "@/lib/appearance";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageViewTracker from "@/components/PageViewTracker";
import ScrollToHash from "@/components/ScrollToHash";
import ClerkGate from "@/components/auth/ClerkGate";
import { TourProvider } from "@/components/tour/TourProvider";
import TourOverlay from "@/components/tour/TourOverlay";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import SeoHead from "@/components/SeoHead";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import JoinWaitlist from "@/pages/JoinWaitlist";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import FreeScan from "@/pages/FreeScan";
import FeedbackWidget from "@/components/FeedbackWidget";
import { useAuth } from "@/lib/auth-context";

// Route-level code splitting: everything behind auth is a separate chunk, so a
// signed-out visitor downloads the marketing pages only. Eager imports above
// are the ones needed for first paint or the auth handshake.
const Pricing = lazyWithRetry(() => import("@/pages/Pricing"), "@/pages/Pricing");
const Features = lazyWithRetry(() => import("@/pages/Features"), "@/pages/Features");
const UseCases = lazyWithRetry(() => import("@/pages/UseCases"), "@/pages/UseCases");
const FAQ = lazyWithRetry(() => import("@/pages/FAQ"), "@/pages/FAQ");
const Security = lazyWithRetry(() => import("@/pages/Security"), "@/pages/Security");
const Contact = lazyWithRetry(() => import("@/pages/Contact"), "@/pages/Contact");
const Demo = lazyWithRetry(() => import("@/pages/Demo"), "@/pages/Demo");
const Compare = lazyWithRetry(() => import("@/pages/Compare"), "@/pages/Compare");
const Guides = lazyWithRetry(() => import("@/pages/Guides"), "@/pages/Guides");
const Guide = lazyWithRetry(() => import("@/pages/Guide"), "@/pages/Guide");
const Docs = lazyWithRetry(() => import("@/pages/Docs"), "@/pages/Docs");
const DocsArticle = lazyWithRetry(() => import("@/pages/DocsArticle"), "@/pages/DocsArticle");
const Privacy = lazyWithRetry(() => import("@/pages/Privacy"), "@/pages/Privacy");
const Terms = lazyWithRetry(() => import("@/pages/Terms"), "@/pages/Terms");
const Onboarding = lazyWithRetry(() => import("@/pages/Onboarding"), "@/pages/Onboarding");
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"), "@/pages/Dashboard");
const ResumeLibrary = lazyWithRetry(() => import("@/pages/ResumeLibrary"), "@/pages/ResumeLibrary");
const FirstResume = lazyWithRetry(() => import("@/pages/FirstResume"), "@/pages/FirstResume");
const EarlyStart = lazyWithRetry(() => import("@/pages/EarlyStart"), "@/pages/EarlyStart");
const GovernmentJobs = lazyWithRetry(() => import("@/pages/GovernmentJobs"), "@/pages/GovernmentJobs");
const GuardianConsent = lazyWithRetry(() => import("@/pages/GuardianConsent"), "@/pages/GuardianConsent");
const ResumeEditor = lazyWithRetry(() => import("@/pages/ResumeEditor"), "@/pages/ResumeEditor");
const JobList = lazyWithRetry(() => import("@/pages/JobList"), "@/pages/JobList");
const JobNew = lazyWithRetry(() => import("@/pages/JobNew"), "@/pages/JobNew");
const JobDetail = lazyWithRetry(() => import("@/pages/JobDetail"), "@/pages/JobDetail");
const JobSearch = lazyWithRetry(() => import("@/pages/JobSearch"), "@/pages/JobSearch");
const JobRadar = lazyWithRetry(() => import("@/pages/JobRadar"), "@/pages/JobRadar");
const Tracker = lazyWithRetry(() => import("@/pages/Tracker"), "@/pages/Tracker");
const Profile = lazyWithRetry(() => import("@/pages/Profile"), "@/pages/Profile");
const Appearance = lazyWithRetry(() => import("@/pages/Appearance"), "@/pages/Appearance");
const AdminDashboard = lazyWithRetry(() => import("@/pages/AdminDashboard"), "@/pages/AdminDashboard");
const STARStoryBank = lazyWithRetry(() => import("@/pages/STARStoryBank"), "@/pages/STARStoryBank");
const CheckoutSuccess = lazyWithRetry(() => import("@/pages/CheckoutSuccess"), "@/pages/CheckoutSuccess");
const ForgotPassword = lazyWithRetry(() => import("@/pages/ForgotPassword"), "@/pages/ForgotPassword");
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"), "@/pages/ResetPassword");
const VerifyEmail = lazyWithRetry(() => import("@/pages/VerifyEmail"), "@/pages/VerifyEmail");

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth session_id in URL fragment BEFORE routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    {/* Inside the router, outside Routes: sees every navigation regardless of
        which route renders. */}
    <PageViewTracker />
    {/* Also inside the router, outside Routes: React Router does not scroll to
        a fragment, so /features#mcp landed at the top of the page. */}
    <ScrollToHash />
    {/* Applies the route's title, description, canonical and JSON-LD. One
        mount, so a new page cannot ship without metadata by forgetting a hook. */}
    <SeoHead />
    {/* Outside Suspense, so it catches a chunk that fails to arrive as well as
        one that throws once it has. */}
    <RouteErrorBoundary>
    <Suspense fallback={<div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/features" element={<Features />} />
      {/* The two other doors' front pages. Same product, same index. */}
      <Route path="/early" element={<EarlyStart />} />
      <Route path="/government" element={<GovernmentJobs />} />
      {/* The consent email links here; the guardian has no account. */}
      <Route path="/guardian-consent" element={<GuardianConsent />} />
      <Route path="/use-cases" element={<UseCases />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/security" element={<Security />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/waitlist" element={<JoinWaitlist />} />
      {/* Public, unauthenticated: the pre-signup front door.
          The canonical URL carries the phrase people search for. /scan stays
          as a redirect rather than a second route — two URLs serving one page
          split the ranking between them and make Search Console pick a winner
          on our behalf. Every existing link, the nav and the announcement bar
          keep working through it. */}
      {/* The guide cluster: informational pages that feed the checker. */}
      <Route path="/compare" element={<Compare />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="/guides/:slug" element={<Guide />} />
      {/* Public on purpose: someone deciding whether this is worth their
          evening should be able to read exactly what it does — and what it
          refuses to do — without making an account. Metadata and sitemap
          entries are generated from the same catalogue the pages render. */}
      <Route path="/docs" element={<Docs />} />
      <Route path="/docs/:slug" element={<DocsArticle />} />
      <Route path="/ats-resume-checker" element={<FreeScan />} />
      <Route path="/scan" element={<Navigate to="/ats-resume-checker" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* The verification email links here. src/__tests__/emailed-links.test.js
          holds every emailed path against this router, because this one was
          missing for weeks and every new user's first click was a 404. */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/resumes" element={<ProtectedRoute><ResumeLibrary /></ProtectedRoute>} />
      {/* Before the :resumeId route, or "first" is read as an id. */}
      <Route path="/resumes/first" element={<ProtectedRoute><FirstResume /></ProtectedRoute>} />
      <Route path="/resumes/:resumeId" element={<ProtectedRoute><ResumeEditor /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobList /></ProtectedRoute>} />
      <Route path="/jobs/new" element={<ProtectedRoute><JobNew /></ProtectedRoute>} />
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><JobSearch /></ProtectedRoute>} />
      <Route path="/radar" element={<ProtectedRoute><JobRadar /></ProtectedRoute>} />
      <Route path="/tracker" element={<ProtectedRoute><Tracker /></ProtectedRoute>} />
      <Route path="/stars" element={<ProtectedRoute><STARStoryBank /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/appearance" element={<ProtectedRoute><Appearance /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
      {/* A real 404, not a redirect to the sales page — see NotFound.jsx. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
    </>
  );
}

export default function App() {
  return (
    <ClerkGate>
    <AuthProvider>
      {/* Colour mode, typeface, corners. Needs the signed-in user (to sync
          with the account) and nothing from the router. */}
      <AppearanceProvider>
      <BrowserRouter>
        {/* Inside BrowserRouter because a tour step can live on another page
            and the provider navigates to it. */}
        <TourProvider>
        {/* Feedback for pages without a bar of their own (public pages).
            App pages mount one in the shell, and this one steps aside. */}
        <NoticeBar fallback className="top-0 px-6" />
        <AppRouter />
        <AuthenticatedExtras />
        </TourProvider>
      </BrowserRouter>
      </AppearanceProvider>
    </AuthProvider>
    </ClerkGate>
  );
}

// Shows the feedback widget only when the user is signed in
function AuthenticatedExtras() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <FeedbackWidget />
      <TourOverlay />
    </>
  );
}
